import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { Users, Mail, Shield, Trash2, Crown, UserPlus, ShoppingCart, Loader2, Copy, Check, Activity as ActivityIcon, ArrowUpRight, XCircle } from "lucide-react";

/**
 * Team management — invite, remove, buy seats, per-seat roles, activity log.
 *
 * Light theme matches the rest of the in-app shell.  Seat purchase is inline
 * (Starter only) and routes to Stripe Checkout.  The activity panel is fed by
 * GET /api/team/activity and shows the last 50 team-scoped events.
 */

const ROLE_BADGE = {
  owner: { label: "OWNER", cls: "bg-amber-50 text-amber-700 border-amber-200", Icon: Crown },
  admin: { label: "ADMIN", cls: "bg-violet-50 text-violet-700 border-violet-200", Icon: Shield },
  member: { label: "MEMBER", cls: "bg-slate-50 text-slate-600 border-slate-200", Icon: Users },
};

const EVENT_COPY = {
  team_invited: (e) => `${e.actor_name || "Someone"} invited ${e.meta?.email || "a teammate"} as ${(e.meta?.role || "member").toUpperCase()}`,
  team_invite_revoked: (e) => `${e.actor_name || "Someone"} revoked the invite for ${e.meta?.email || "a teammate"}`,
  team_invite_accepted: (e) => `${e.actor_name || e.meta?.email || "Teammate"} accepted the invite as ${(e.meta?.role || "member").toUpperCase()}`,
  team_role_changed: (e) => `${e.actor_name || "Someone"} changed a teammate's role to ${(e.meta?.role || "member").toUpperCase()}`,
  team_member_removed: (e) => `${e.actor_name || "Someone"} removed ${e.meta?.email || e.meta?.name || "a teammate"} from the workspace`,
  team_seats_purchased: (e) => `${e.actor_name || "Owner"} purchased ${e.meta?.quantity || 1} extra seat${(e.meta?.quantity || 1) === 1 ? "" : "s"} · $${((e.meta?.amount_cents || 0) / 100).toFixed(0)}`,
};

function timeAgo(iso) {
  if (!iso) return "—";
  const then = new Date(iso).getTime();
  const diff = Math.floor((Date.now() - then) / 1000);
  if (diff < 60) return `${diff}s ago`;
  if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
  return `${Math.floor(diff / 86400)}d ago`;
}

export default function Team() {
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [activity, setActivity] = useState([]);
  const [inviteEmail, setInviteEmail] = useState("");
  const [inviteRole, setInviteRole] = useState("member");
  const [busy, setBusy] = useState(false);
  const [buying, setBuying] = useState(false);
  const [buyQty, setBuyQty] = useState(1);
  const [copiedToken, setCopiedToken] = useState("");
  const [confirmRemove, setConfirmRemove] = useState(null);
  const nav = useNavigate();

  const load = async () => {
    try {
      const [t, a] = await Promise.all([api.get("/team"), api.get("/team/activity?limit=50")]);
      setData(t.data);
      setActivity(a.data?.events || []);
    } catch (err) {
      // Best-effort background load — surface the error to the console so
      // ops can debug 4xx/5xx spikes without a user-facing toast on every refresh.
      console.error("Team load failed:", err);
    }
  };
  useEffect(() => { load(); }, []);

  const invite = async (e) => {
    e.preventDefault();
    if (!inviteEmail) return;
    setBusy(true);
    try {
      await api.post("/team/invite", { email: inviteEmail, role: inviteRole });
      toast.success("Invitation created");
      setInviteEmail("");
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Invite failed"); }
    finally { setBusy(false); }
  };

  const revokeInvite = async (invite_id) => {
    try { await api.del(`/team/invite/${invite_id}`); load(); toast.success("Revoked"); } catch (err) { console.error("revoke invite failed:", err); toast.error("Failed"); }
  };

  const removeMember = async (uid) => {
    try { await api.del(`/team/${uid}`); setConfirmRemove(null); load(); toast.success("Removed"); } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  const changeRole = async (uid, role) => {
    try { await api.put(`/team/${uid}/role`, { role }); load(); toast.success("Role updated"); } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  const buySeats = async () => {
    setBuying(true);
    try {
      const r = await api.post("/team/buy-seats", { quantity: buyQty, origin_url: window.location.origin });
      window.location.href = r.data.checkout_url;
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); setBuying(false); }
  };

  const inviteLink = (token) => `${window.location.origin}/invite/${token}`;
  const copy = (token) => {
    navigator.clipboard.writeText(inviteLink(token));
    setCopiedToken(token);
    setTimeout(() => setCopiedToken(""), 1500);
  };

  const isAdmin = user?.role === "owner" || user?.role === "admin";
  const isOwner = user?.role === "owner";

  const seatsUsedPct = useMemo(() => {
    if (!data) return 0;
    return Math.min(100, Math.round((data.seats_used / Math.max(1, data.seat_capacity)) * 100));
  }, [data]);

  if (!data) return <div className="p-8 text-slate-400">Loading…</div>;

  return (
    <div className="p-8 max-w-6xl" data-testid="team-page">
      <div className="mb-8">
        <div className="text-[11px] font-mono font-bold tracking-widest text-emerald-600 mb-2">// WORKSPACE ADMIN</div>
        <h1 className="font-display font-black text-4xl tracking-tight text-slate-900">Team</h1>
        <p className="text-slate-500 mt-2">Manage seats, roles, and access. Every action lands in the activity log.</p>
      </div>

      {/* Seat capacity — light theme card */}
      <div className="rounded-2xl border border-slate-200 bg-white p-6 mb-6 shadow-sm" data-testid="seat-panel">
        <div className="grid md:grid-cols-4 gap-6">
          <div>
            <div className="text-[10px] font-mono font-bold tracking-widest text-slate-500 mb-2">CURRENT PLAN</div>
            <div className="font-display font-black text-2xl capitalize text-slate-900">{data.plan}</div>
            <Link to={`/pricing?from=${data.plan}`} className="mt-2 inline-flex items-center gap-1 text-[11px] font-mono font-bold tracking-widest text-violet-600 hover:text-violet-700" data-testid="upgrade-from-team">
              UPGRADE <ArrowUpRight className="w-3 h-3" />
            </Link>
          </div>
          <div>
            <div className="text-[10px] font-mono font-bold tracking-widest text-slate-500 mb-2">SEATS USED</div>
            <div className="font-display font-black text-2xl text-slate-900" data-testid="seats-used">{data.seats_used} <span className="text-slate-400">/ {data.seat_capacity}</span></div>
            <div className="h-1.5 bg-slate-100 mt-2 rounded-full overflow-hidden">
              <div
                className={`h-full transition-all duration-500 ${seatsUsedPct >= 100 ? "bg-rose-500" : seatsUsedPct >= 80 ? "bg-amber-500" : "bg-gradient-to-r from-violet-500 to-fuchsia-500"}`}
                style={{ width: `${seatsUsedPct}%` }}
              />
            </div>
          </div>
          <div>
            <div className="text-[10px] font-mono font-bold tracking-widest text-slate-500 mb-2">EXTRA SEATS</div>
            <div className="font-display font-black text-2xl text-slate-900">{data.extra_seats}</div>
            {data.can_buy_extra && <div className="text-xs text-slate-500 mt-1">${data.extra_seat_price_cents / 100}/seat/mo</div>}
          </div>
          <div className="flex items-end">
            {data.can_buy_extra && isOwner && data.extra_seats < data.extra_seat_max ? (
              <div className="flex items-center gap-2 w-full">
                <select value={buyQty} onChange={(e) => setBuyQty(Number(e.target.value))} className="bg-white border border-slate-200 focus:border-violet-400 px-3 py-2 text-sm rounded-lg outline-none" data-testid="buy-qty">
                  {Array.from({ length: data.extra_seat_max - data.extra_seats }, (_, i) => i + 1).map(n => <option key={n} value={n}>+{n} seat{n>1?"s":""}</option>)}
                </select>
                <button
                  onClick={buySeats}
                  disabled={buying}
                  className="flex-1 bg-gradient-to-r from-violet-500 to-fuchsia-500 text-white px-4 py-2 text-xs font-mono font-bold tracking-widest rounded-lg hover:from-violet-600 hover:to-fuchsia-600 disabled:opacity-50 flex items-center justify-center gap-1"
                  data-testid="buy-seats-btn"
                >
                  {buying ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <ShoppingCart className="w-3.5 h-3.5" />}BUY
                </button>
              </div>
            ) : (
              <Link
                to={`/pricing?from=${data.plan}&required=professional`}
                className="w-full text-center border border-slate-200 hover:border-violet-400 hover:text-violet-600 px-4 py-2 text-xs font-mono font-bold tracking-widest rounded-lg transition-colors"
                data-testid="upgrade-plan"
              >
                UPGRADE PLAN
              </Link>
            )}
          </div>
        </div>
      </div>

      {/* Invite */}
      {isAdmin && data.seats_used < data.seat_capacity && (
        <form onSubmit={invite} className="rounded-2xl border border-violet-200 bg-gradient-to-br from-violet-50/60 to-white p-5 mb-6 flex flex-wrap items-end gap-3" data-testid="invite-form">
          <div className="flex-1 min-w-[220px]">
            <div className="text-[10px] font-mono font-bold tracking-widest text-slate-500 mb-2">EMAIL</div>
            <input type="email" required value={inviteEmail} onChange={(e) => setInviteEmail(e.target.value)} placeholder="teammate@company.com" className="w-full bg-white border border-slate-200 focus:border-violet-400 px-3 py-2.5 text-sm rounded-lg outline-none" data-testid="invite-email" />
          </div>
          <div>
            <div className="text-[10px] font-mono font-bold tracking-widest text-slate-500 mb-2">ROLE</div>
            <select value={inviteRole} onChange={(e) => setInviteRole(e.target.value)} className="bg-white border border-slate-200 focus:border-violet-400 px-3 py-2.5 text-sm rounded-lg outline-none" data-testid="invite-role">
              <option value="member">Member</option>
              <option value="admin">Admin</option>
            </select>
          </div>
          <button type="submit" disabled={busy} className="bg-gradient-to-r from-violet-500 to-fuchsia-500 text-white px-5 py-2.5 text-xs font-mono font-bold tracking-widest rounded-lg hover:from-violet-600 hover:to-fuchsia-600 disabled:opacity-50 flex items-center gap-2" data-testid="invite-submit">
            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <UserPlus className="w-4 h-4" />}INVITE
          </button>
        </form>
      )}

      <div className="grid lg:grid-cols-[1fr_360px] gap-6">
        <div className="space-y-6">
          {/* Members */}
          <div className="rounded-2xl border border-slate-200 bg-white overflow-hidden shadow-sm">
            <div className="px-5 py-4 border-b border-slate-100 flex items-center justify-between">
              <div className="text-[10px] font-mono font-bold tracking-widest text-slate-500 flex items-center gap-2"><Users className="w-3.5 h-3.5" />MEMBERS · {data.members.length}</div>
              <div className="text-[10px] font-mono font-bold tracking-widest text-slate-400">{data.members.length} of {data.seat_capacity}</div>
            </div>
            {data.members.map(m => {
              const badge = ROLE_BADGE[m.role] || ROLE_BADGE.member;
              const Icon = badge.Icon;
              return (
                <div key={m.user_id} className="px-5 py-4 border-b border-slate-100 last:border-b-0 flex items-center gap-4" data-testid={`member-${m.user_id}`}>
                  <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-violet-100 to-fuchsia-100 border border-violet-200 text-violet-700 flex items-center justify-center shrink-0 font-display font-black">{(m.name || m.email)[0]?.toUpperCase()}</div>
                  <div className="flex-1 min-w-0">
                    <div className="font-medium text-slate-900 truncate">{m.name}</div>
                    <div className="text-xs text-slate-500 truncate">{m.email}</div>
                  </div>
                  <span className={`inline-flex items-center gap-1.5 border px-2.5 py-1 rounded-full text-[10px] font-mono font-bold tracking-widest ${badge.cls}`}>
                    <Icon className="w-3 h-3" />{badge.label}
                  </span>
                  {isOwner && !m.is_owner && (
                    <>
                      <select value={m.role} onChange={(e) => changeRole(m.user_id, e.target.value)} className="bg-white border border-slate-200 focus:border-violet-400 px-2 py-1 text-xs font-mono rounded-lg outline-none" data-testid={`role-${m.user_id}`}>
                        <option value="member">Member</option>
                        <option value="admin">Admin</option>
                      </select>
                      <button onClick={() => setConfirmRemove(m)} className="text-slate-400 hover:text-rose-500 p-1 transition-colors" data-testid={`remove-${m.user_id}`}><Trash2 className="w-4 h-4" /></button>
                    </>
                  )}
                </div>
              );
            })}
          </div>

          {/* Pending invitations */}
          {data.invites.length > 0 && (
            <div className="rounded-2xl border border-slate-200 bg-white overflow-hidden shadow-sm">
              <div className="px-5 py-4 border-b border-slate-100 text-[10px] font-mono font-bold tracking-widest text-slate-500 flex items-center gap-2">
                <Mail className="w-3.5 h-3.5" />PENDING INVITATIONS · {data.invites.length}
              </div>
              {data.invites.map(inv => (
                <div key={inv.invite_id} className="px-5 py-4 border-b border-slate-100 last:border-b-0 flex items-center gap-4 flex-wrap" data-testid={`invite-${inv.invite_id}`}>
                  <div className="flex-1 min-w-[220px]">
                    <div className="font-medium text-slate-900">{inv.email}</div>
                    <div className="text-[10px] font-mono font-bold tracking-widest text-slate-500 mt-1">
                      ROLE: {inv.role.toUpperCase()} · EXPIRES {new Date(inv.expires_at).toLocaleDateString()}
                    </div>
                  </div>
                  <button onClick={() => copy(inv.token)} className="text-[10px] font-mono font-bold tracking-widest text-violet-600 hover:text-violet-700 flex items-center gap-1.5" data-testid={`copy-${inv.invite_id}`}>
                    {copiedToken === inv.token ? <><Check className="w-3.5 h-3.5" />COPIED</> : <><Copy className="w-3.5 h-3.5" />COPY LINK</>}
                  </button>
                  {isAdmin && <button onClick={() => revokeInvite(inv.invite_id)} className="text-slate-400 hover:text-rose-500 p-1 transition-colors" data-testid={`revoke-${inv.invite_id}`}><Trash2 className="w-4 h-4" /></button>}
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Activity panel */}
        <div className="rounded-2xl border border-slate-200 bg-white overflow-hidden shadow-sm h-fit" data-testid="team-activity">
          <div className="px-5 py-4 border-b border-slate-100 text-[10px] font-mono font-bold tracking-widest text-slate-500 flex items-center gap-2">
            <ActivityIcon className="w-3.5 h-3.5" />ACTIVITY LOG · {activity.length}
          </div>
          {activity.length === 0 ? (
            <div className="p-6 text-sm text-slate-400 text-center">
              No team activity yet. Invite a teammate to see the first entry.
            </div>
          ) : (
            <div className="max-h-[480px] overflow-y-auto">
              {activity.map((e) => {
                const copy = EVENT_COPY[e.action]?.(e) || `${e.action.replaceAll("_", " ")}`;
                return (
                  <div key={e.activity_id} className="px-5 py-3 border-b border-slate-100 last:border-b-0 text-sm" data-testid={`activity-${e.activity_id}`}>
                    <div className="text-slate-700 leading-snug">{copy}</div>
                    <div className="text-[10px] font-mono font-bold tracking-widest text-slate-400 mt-1">{timeAgo(e.created_at)}</div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>

      {/* Confirm remove dialog */}
      {confirmRemove && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-900/60 backdrop-blur-sm" data-testid="confirm-remove-dialog">
          <div className="relative w-full max-w-md rounded-2xl bg-white shadow-2xl border border-slate-200 overflow-hidden">
            <div className="px-6 pt-5 pb-4 border-b border-slate-200 flex items-start gap-3">
              <div className="w-10 h-10 rounded-xl bg-rose-100 text-rose-600 flex items-center justify-center shrink-0">
                <XCircle className="w-5 h-5" />
              </div>
              <div>
                <div className="text-[10px] font-mono font-bold tracking-widest text-rose-600">REMOVE MEMBER</div>
                <div className="text-lg font-display font-bold text-slate-900 leading-tight mt-0.5">
                  Remove {confirmRemove.name || confirmRemove.email}?
                </div>
              </div>
            </div>
            <div className="px-6 py-5 text-sm text-slate-600">
              They'll lose access to this workspace, but keep their BidForge account. Their pipeline history stays with the workspace.
            </div>
            <div className="px-6 py-4 bg-slate-50 border-t border-slate-200 flex items-center justify-between gap-3">
              <button
                onClick={() => setConfirmRemove(null)}
                className="text-sm font-mono font-bold tracking-widest text-slate-600 hover:text-slate-900"
                data-testid="confirm-remove-cancel"
              >CANCEL</button>
              <button
                onClick={() => removeMember(confirmRemove.user_id)}
                className="px-4 py-2 rounded-full text-sm font-mono font-bold tracking-widest text-white bg-rose-500 hover:bg-rose-600 shadow-md"
                data-testid="confirm-remove-confirm"
              >REMOVE</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
